/**
 * Issue #241 — per-order Kalshi cancel lifecycle (pure).
 *
 * A successful DELETE is not evidence the order is gone. The relay's Open
 * Orders read is org-snapshot backed, so the refresh fired straight after a
 * cancel can still return the pre-cancel book; the row would briefly re-enable
 * and a second click would send a duplicate DELETE that fails misleadingly
 * *after* the first cancel actually worked (Grim, 2026-07-31).
 *
 * So the fence lives here: an order stays pending from the moment its cancel is
 * requested until a successfully parsed authoritative snapshot no longer lists
 * it — with a bounded 5,000 ms escape so a stalled sync can't disable the
 * control forever.
 *
 * Deliberately free of timers, React, Zustand, network and order parsing: the
 * store owns the clock and the transport, this module owns the transitions.
 * Every function returns the input map by identity when nothing changed, so a
 * quiet poll cannot churn `kalshiCancelStates` and re-render every order row.
 */

/** Bounded escape from a stalled Open Orders synchronization. */
export const KALSHI_CANCEL_SYNC_TIMEOUT_MS = 5_000;

/** Truthful copy: the cancel succeeded, the *confirmation* is what's missing.
 *  Derived from the constant above so the two can never drift apart. */
export const KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE =
  `Cancel succeeded, but Open Orders still shows this order after ${
    KALSHI_CANCEL_SYNC_TIMEOUT_MS / 1_000
  } seconds. Verify synchronization before retrying.`;

export type KalshiCancelPhase =
  /** DELETE is in flight. */
  | 'requesting'
  /** DELETE succeeded; waiting for the ID to leave the authoritative list. */
  | 'awaiting-removal'
  /** Waited past the timeout. Retryable — NOT proof the order survived. */
  | 'timed-out';

export interface KalshiCancelState {
  phase: KalshiCancelPhase;
  /** Phase start. Restamped on DELETE success — that instant starts the window. */
  startedAtMs: number;
  /** Set only on 'timed-out'. */
  message?: string;
}

export type KalshiCancelStates = Readonly<Partial<Record<string, KalshiCancelState>>>;

/**
 * Per-ID outcome of a batched cancel (#272). Lives here, beside the state
 * machine it reports on, so `accountStore` does not have to import the bulk
 * executor to describe its own return type (`bulkOrderCancel` re-exports it as
 * part of the executor's public surface).
 *
 * `submitted` is deliberately not called `cancelled`: a 2xx DELETE is a request
 * the venue accepted, and the order is only proven gone once an authoritative
 * Open Orders snapshot stops listing it.
 */
export interface KalshiCancelBatchResult {
  orderId: string;
  status: 'submitted' | 'failed' | 'already-pending';
  error: string | null;
}

/** The row's Cancel/Edit controls are disabled exactly while this is true.
 *  'timed-out' is deliberately NOT pending: the trader gets the control back. */
export function isKalshiCancelPending(state: KalshiCancelState | undefined): boolean {
  return state?.phase === 'requesting' || state?.phase === 'awaiting-removal';
}

/** Fence an ID as its DELETE goes out. Also the retry entry point — it drops any
 *  previous timed-out state (and its message) for the same ID. */
export function beginKalshiCancel(
  states: KalshiCancelStates,
  orderId: string,
  nowMs: number,
): KalshiCancelStates {
  return { ...states, [orderId]: { phase: 'requesting', startedAtMs: nowMs } };
}

/** DELETE resolved 2xx. Only promotes an attempt that is still 'requesting': if
 *  an authoritative snapshot (or a disconnect) cleared the ID while the request
 *  was in flight, a late success must not resurrect the fence. */
export function markKalshiCancelAwaitingRemoval(
  states: KalshiCancelStates,
  orderId: string,
  nowMs: number,
): KalshiCancelStates {
  if (states[orderId]?.phase !== 'requesting') return states;
  return { ...states, [orderId]: { phase: 'awaiting-removal', startedAtMs: nowMs } };
}

/** The 5,000 ms window elapsed with the ID still listed. Only an entry that is
 *  actually awaiting removal can time out — a stale timer must not promote the
 *  fresh 'requesting' state of a later retry. */
export function markKalshiCancelTimedOut(
  states: KalshiCancelStates,
  orderId: string,
): KalshiCancelStates {
  const current = states[orderId];
  if (current?.phase !== 'awaiting-removal') return states;
  return {
    ...states,
    [orderId]: { ...current, phase: 'timed-out', message: KALSHI_CANCEL_SYNC_TIMEOUT_MESSAGE },
  };
}

/** Drop one ID's state (DELETE rejected, or disconnect). */
export function clearKalshiCancel(
  states: KalshiCancelStates,
  orderId: string,
): KalshiCancelStates {
  if (states[orderId] === undefined) return states;
  const next = { ...states };
  delete next[orderId];
  return next;
}

/**
 * The authoritative step. Given the order-ID set of a *successfully parsed*
 * snapshot, drop every fenced ID the venue no longer lists — that disappearance
 * is the only real cancellation confirmation. IDs still present stay fenced,
 * which is what keeps a stale post-cancel refresh from re-enabling the row.
 *
 * Call this ONLY for a snapshot that passed `parseOrdersSnapshot`. A failed or
 * malformed read is not evidence of anything and must not reach here.
 */
export function reconcileKalshiCancels(
  states: KalshiCancelStates,
  authoritativeOrderIds: Iterable<string>,
): KalshiCancelStates {
  const fenced = Object.keys(states);
  if (fenced.length === 0) return states; // the overwhelmingly common poll
  const live = new Set(authoritativeOrderIds);
  const removed = fenced.filter((orderId) => !live.has(orderId));
  if (removed.length === 0) return states;
  const next = { ...states };
  for (const orderId of removed) delete next[orderId];
  return next;
}
